/**
 * "Giá đất" – Bảng giá đất TP Hà Nội áp dụng từ 01/01/2026 (Nghị quyết 52/2025/NQ-HĐND):
 *  - panel tab: phường / xã, khu vực, the priced roads around the pick (VT1–VT4 for đất ở, đất
 *    thương mại dịch vụ, đất SXKD), streets of the ward's new urban areas, rural residential areas,
 *    agricultural land, how the position (VT) is determined;
 *  - map layer: road segments coloured by the VT1 residential price (Lớp bản đồ → Bảng giá đất);
 *  - dialog: the whole table of a khu vực, searchable, a click shows the segment on the map.
 * Static data in public/data/gia (see core/gia.js); segment positions are estimated from
 * OpenStreetMap, so the "Từ – Đến" text of the table is always shown next to them.
 */
import { $, esc, icon, fmt, isMobile, toast } from './ui.js';
import { bus, prefs, savePrefs } from './state.js';
import { vurl } from './assetv.js';
import { act } from './telemetry.js';
import {
  LAND, CLASS_STEPS, CLASS_COLORS, rowOf, rowTitle, rowSegment, trieu, wardAt, areasNear, nearestRows, rowGeometry,
  geomBBox, mentions, agriOf, searchRows, wardName, prepArea,
} from './core/gia.js';

const BASE = '/data/gia/';
const MAX_D = 200;
let pm = null, index = null, indexP = null, land = 0;
const areas = new Map(); // a -> Promise<area>
const loaded = new Map(); // a -> area (resolved)

const nf = (x, d) => fmt.num(x, d, d);
const mDist = (d) => (d < 1000 ? `${Math.max(1, Math.round(d))} m` : fmt.dist(d));
const tr = (v) => trieu(v, nf);
const ngh = (v) => (v == null ? '—' : fmt.num(v)); // thousand đ/m² as printed in the table

export function loadGiaIndex() {
  if (index) return Promise.resolve(index);
  indexP ||= fetch(vurl(BASE + 'index.json', 'gia')).then((r) => { if (!r.ok) throw new Error('Bảng giá ' + r.status); return r.json(); })
    .then((j) => { index = j; return j; }).catch((e) => { indexP = null; throw e; });
  return indexP;
}

export function loadArea(a) {
  if (!areas.has(a)) {
    areas.set(a, fetch(vurl(`${BASE}a${a}.json`, 'gia')).then((r) => { if (!r.ok) throw new Error('Bảng giá khu vực ' + a + ': ' + r.status); return r.json(); })
      .then((j) => {
        prepArea(j);
        j.byStreet = new Map();
        j.rows.forEach((r, i) => { const k = r[0] + '|' + r[2]; if (!j.byStreet.has(k)) j.byStreet.set(k, []); j.byStreet.get(k).push(i); });
        loaded.set(a, j);
        return j;
      })
      .catch((e) => { areas.delete(a); throw e; }));
  }
  return areas.get(a);
}

const areaInfo = (a) => index?.areas.find((x) => x.a === a);
const streetRows = (A, r) => (A.byStreet.get(r.tt + '|' + r.s) || [r.i]).map((i) => rowOf(A, i));

/* ---------------- lookup for a pick ---------------- */

/** Everything the tab shows for a point. */
export async function giaAt(lat, lng) {
  const idx = await loadGiaIndex();
  const ward = wardAt(idx, lat, lng);
  const as = await Promise.all(areasNear(idx, lat, lng).map(loadArea));
  const hits = nearestRows(as, lat, lng, MAX_D);
  const byA = new Map(as.map((A) => [A.a, A]));
  // one card per street: its nearest segment(s); "amb" lines belong to several segments of the street
  const cards = [];
  const seen = new Map();
  for (const h of hits) {
    const A = byA.get(h.a), r = rowOf(A, h.i);
    const k = h.a + '|' + r.tt + '|' + r.s;
    let c = seen.get(k);
    if (!c) {
      if (cards.length >= 6) continue;
      c = { a: h.a, d: h.d, rows: [], amb: h.amb };
      seen.set(k, c); cards.push(c);
    }
    if (h.d <= c.d + (c.amb ? 1 : 0.01)) c.rows.push({ ...r, d: h.d });
  }
  for (const c of cards) {
    const A = byA.get(c.a);
    const ids = new Set(c.rows.map((r) => r.i));
    c.others = streetRows(A, c.rows[0]).filter((r) => !ids.has(r.i));
  }
  const res = { ward, area: ward?.a || null, cards, kdt: [], kdc: [], more: [], agri: agriOf(idx, ward) };
  const A = ward && byA.get(ward.a);
  if (A) {
    const used = new Set(cards.flatMap((c) => c.rows.map((r) => r.i)));
    for (let i = 0; i < A.rows.length; i++) {
      const r = rowOf(A, i);
      const text = `${r.s} ${r.f} ${r.t}`;
      if (r.g === 3) { if (mentions(text, ward.n)) res.kdt.push(r); }
      else if (r.g === 4) { if (mentions(r.s, ward.n)) res.kdc.push(r); }
      else if (A.rural && !used.has(i) && mentions(text, ward.n)) res.more.push(r);
    }
  }
  return res;
}

/**
 * Reference line of the map hover card: VT1 residential price of the nearest priced road segment,
 * from data already loaded (no request; null when the table is not loaded or no road is near).
 */
export function giaHint(lat, lng, maxD = 80) {
  if (!index) return null;
  const as = areasNear(index, lat, lng).map((a) => loaded.get(a)).filter(Boolean);
  if (!as.length) return null;
  let best = null;
  for (const h of nearestRows(as, lat, lng, maxD)) if (!best || h.d < best.d) best = h;
  const A = best && as.find((x) => x.a === best.a);
  const r = A && rowOf(A, best.i);
  const p = r?.p?.[0];
  if (!p) return null;
  return `Giá đất ở VT1 ${r.s}: ${tr(p)} tr/m²`;
}

/* ---------------- panel tab ---------------- */

const landSeg = () => `<div class="seg gia-land" role="tablist" aria-label="Loại đất">${LAND.map(([, l], k) => `<button type="button" role="tab" data-act="gia-land" data-k="${k}" aria-selected="${land === k}">${esc(k === 0 ? 'Đất ở' : k === 1 ? 'TMDV' : 'SXKD')}</button>`).join('')}</div>`;

function vtGrid(p, kdt = false) {
  const o = land * 4;
  const n = kdt ? 2 : 4;
  return `<div class="gia-vt">${Array.from({ length: n }, (_, k) => `<div class="${k === 0 ? 'vt1' : ''}"><span>VT${k + 1}</span><b>${tr(p[o + k])}</b></div>`).join('')}</div>`;
}

function rowLine(r, { dist } = {}) {
  const seg = rowSegment(r);
  return `<button type="button" class="gia-row" data-act="gia-row" data-a="${r.a}" data-i="${r.i}" ${r.g === 1 || r.g === 2 ? '' : 'data-nogeo="1"'}>
    <span class="grow"><span class="t">${esc(seg || rowTitle(r))}</span>${dist != null ? `<span class="d">cách ~${mDist(dist)}</span>` : ''}</span>
    <span class="p">${tr(r.p[land * 4])}</span>
  </button>`;
}

function card(c) {
  const r0 = c.rows[0];
  const title = r0.rural && r0.f ? r0.f : r0.s;
  const sub = r0.rural ? (r0.f ? r0.s : r0.sec) : rowSegment(r0);
  const amb = c.rows.length > 1;
  const badges = [`<span class="badge">${icon('map-pin')}cách ~${esc(mDist(c.d))}</span>`];
  if (amb || r0.g === 2) badges.push(`<span class="badge warn" data-tip="Đường có nhiều đoạn giá; chưa xác định được đoạn tại vị trí này">Chưa tách đoạn</span>`);
  return `<section class="gia-card" style="--gc:${CLASS_COLORS[classIdx(r0.p[0])]}">
    <button type="button" class="gia-card-head" data-act="gia-row" data-a="${r0.a}" data-i="${r0.i}">
      <span class="gia-dot" aria-hidden="true"></span>
      <span class="grow"><span class="gia-name">${esc(title)}</span>${sub && !amb ? `<span class="gia-seg">${esc(sub)}</span>` : ''}</span>
      ${icon('map-pinned', 'sm')}
    </button>
    <div class="badges">${badges.join('')}</div>
    ${amb ? `<div class="gia-rows">${c.rows.map((r) => rowLine(r)).join('')}</div>` : vtGrid(r0.p, r0.g === 3)}
    ${c.others.length ? `<details class="gia-more"><summary>${icon('chevron-right', 'sm')}Các đoạn khác của tuyến (${c.others.length})</summary><div class="gia-rows">${c.others.map((r) => rowLine(r)).join('')}</div></details>` : ''}
  </section>`;
}
const classIdx = (v) => { let i = 0; while (i < CLASS_STEPS.length && v >= CLASS_STEPS[i]) i++; return i; };

function vtNote(g) {
  const c = g.cards[0];
  const near = c && c.d <= 25;
  const name = c ? (c.rows[0].rural && c.rows[0].f ? 'tuyến đường này' : c.rows[0].s) : '';
  return `<details class="gia-rules"${near ? '' : ' open'}>
    <summary>${icon('info', 'sm')}Cách xác định vị trí (VT) và hệ số</summary>
    <ul>
      ${near ? `<li>Thửa đất có ít nhất một mặt giáp <b>${esc(name)}</b> → <b>VT1</b>.</li>` : '<li><b>VT1</b>: thửa đất có ít nhất một mặt giáp đường, phố có tên trong bảng giá.</li>'}
      <li><b>VT2</b>: giáp ngõ có mặt cắt nhỏ nhất (tính từ đường, phố đến thửa đất) <b>từ 3,5 m</b>; <b>VT3</b>: từ 2 m đến dưới 3,5 m; <b>VT4</b>: dưới 2 m.</li>
      <li>Ngõ cách đường, phố từ 200 m: giảm 5% (200–300 m), 10% (300–400 m), 15% (400–500 m); nông thôn từ 500 m tính theo giá khu dân cư nông thôn.</li>
      <li>Thửa có nhiều mặt tiếp giáp: nhân hệ số K (4 mặt K = 1,3; mặt đường và ngõ ≥ 3,5 m K = 1,15; ngõ 2–3,5 m K = 1,1; ngõ &lt; 2 m K = 1,05).</li>
      <li>Thửa đất sâu được chia lớp theo chiều sâu (lớp sau thấp hơn, lớp 4 từ 300 m giảm 30%); thửa “khuất” hình dạng đặc biệt K = 0,9 / 0,8 / 0,7.</li>
    </ul>
  </details>`;
}

function agriBlock(ag) {
  if (!ag?.z?.length) return '';
  const head = ['Lúa, cây hằng năm', 'Cây lâu năm', 'Nuôi trồng thuỷ sản', 'Rừng'];
  const multi = ag.z.length > 1;
  return `<details class="gia-more gia-agri"><summary>${icon('wheat', 'sm')}Đất nông nghiệp <small>đ/m²</small></summary>
    <div class="gia-table-wrap"><table class="gia-t"><thead><tr>${multi ? '<th>Vùng</th>' : ''}${head.map((h) => `<th>${h}</th>`).join('')}</tr></thead>
    <tbody>${ag.z.map(([zone, ...v]) => `<tr>${multi ? `<td>${esc(zone)}</td>` : ''}${v.map((x) => `<td class="num">${x ? fmt.num(x) : '—'}</td>`).join('')}</tr>`).join('')}</tbody></table></div>
  </details>`;
}

function listBlock(title, ico, rows, { kdt = false } = {}) {
  if (!rows.length) return '';
  return `<details class="gia-more"><summary>${icon(ico, 'sm')}${esc(title)} (${rows.length})</summary>
    <div class="gia-table-wrap"><table class="gia-t"><thead><tr><th>${kdt ? 'Khu, mặt cắt đường' : 'Tuyến, đoạn'}</th><th>VT1</th>${kdt ? '<th>VT2</th>' : ''}</tr></thead>
    <tbody>${rows.map((r) => `<tr><td>${esc(rowTitle(r))}${rowSegment(r) && !r.rural ? `<small>${esc(rowSegment(r))}</small>` : ''}</td><td class="num">${tr(r.p[land * 4])}</td>${kdt ? `<td class="num">${tr(r.p[land * 4 + 1])}</td>` : ''}</tr>`).join('')}</tbody></table></div>
  </details>`;
}

/** Tab HTML parts (panel.js staggers them). */
export function giaTab(s) {
  const g = s.gia;
  if (g === undefined || g === null) return ['<div class="sk sk-block" style="height:76px"></div>', '<div class="sk sk-block" style="height:132px"></div>', '<div class="sk sk-block" style="height:132px"></div>'];
  if (g.error) return [`<div class="note warn">${icon('triangle-alert')}<div><b>Không tải được bảng giá đất.</b><br>${esc(g.error)}<div style="margin-top:8px"><button type="button" class="btn sm" data-act="gia-retry">${icon('rotate-ccw')}Thử lại</button></div></div></div>`];
  if (!g.ward) return [`<div class="note">${icon('info')}<div>Vị trí nằm ngoài ranh giới TP Hà Nội, không có trong bảng giá đất của thành phố.</div></div>`];
  const ai = areaInfo(g.area);
  const parts = [];
  parts.push(`<section class="gia-head">
    <div class="eyebrow">${icon('banknote')}Bảng giá đất 2026</div>
    <div class="gia-ward">${esc(g.ward.n)}<span>Khu vực ${g.area}</span></div>
    <div class="gia-src">Áp dụng từ 01/01/2026 · Nghị quyết 52/2025/NQ-HĐND · đơn vị <b>triệu đồng/m²</b></div>
    ${landSeg()}
  </section>`);
  if (g.cards.length) {
    parts.push(`<div class="section-title"><span>Đường, phố gần nhất</span><span class="meta">${esc(LAND[land][1])}</span></div>`);
    for (const c of g.cards) parts.push(card(c));
  } else {
    parts.push(`<div class="note">${icon('info')}<div>Không thấy đoạn đường có tên trong bảng giá trong bán kính ${MAX_D} m (vị trí trong ngõ sâu, khu đô thị hoặc đoạn chưa được định vị). Tra trong bảng giá ${ai ? 'khu vực ' + g.area : ''} theo tên đường.</div></div>`);
  }
  parts.push(vtNote(g));
  const extra = [
    listBlock(`Đường trong khu đô thị, khu tái định cư thuộc ${g.ward.n}`, 'building', g.kdt, { kdt: true }),
    listBlock(`Khu dân cư nông thôn`, 'house', g.kdc),
    listBlock(`Tuyến khác trong địa bàn ${wardName(g.ward.n)} theo bảng giá`, 'route', g.more),
    agriBlock(g.agri),
  ].filter(Boolean).join('');
  if (extra) parts.push(`<div class="gia-extra">${extra}</div>`);
  parts.push(`<div class="gia-actions">
    <button type="button" class="link-row" data-act="gia-table" data-a="${g.area}">${icon('table')}<span class="grow">Bảng giá khu vực ${g.area}<small class="meta"> · ${fmt.num(ai?.n || 0)} dòng</small></span>${icon('chevron-right', 'sm')}</button>
    <button type="button" class="link-row" data-act="gia-layer">${icon('layers')}<span class="grow">${giaLayerOn() ? 'Tắt' : 'Bật'} lớp giá đất trên bản đồ</span>${icon('chevron-right', 'sm')}</button>
  </div>`);
  parts.push(`<div class="note">${icon('info')}<div>Giá đất do Nhà nước quy định (tính tiền sử dụng đất, thuế, bồi thường…), <b>không phải giá thị trường</b>. Vị trí đoạn đường được ước tính từ OpenStreetMap theo tên đường và các mốc “Từ – Đến”; hãy đối chiếu với văn bản của cơ quan có thẩm quyền.</div></div>`);
  return parts;
}

/** Load the tab data for a selection; `done` re-renders. */
export function loadGiaFor(s, done) {
  s.gia = null;
  giaAt(s.lat, s.lng).then((r) => { s.gia = r; done(s); }).catch((e) => { s.gia = { error: e.message || 'Lỗi tải dữ liệu' }; done(s); });
}

/** Short teaser for the planning tab ("Giá đất VT1: … tr/m²"), or ''. */
export function giaTeaser(s) {
  const c = s.gia?.cards?.[0];
  if (!c || c.d > 60) return '';
  const r = c.rows[0];
  const name = r.rural && r.f ? r.f : r.s;
  const v = c.rows.length > 1 ? `${tr(Math.min(...c.rows.map((x) => x.p[0])))}–${tr(Math.max(...c.rows.map((x) => x.p[0])))}` : tr(r.p[0]);
  return `<button type="button" class="link-row gia-teaser" data-tab="gia" style="--gc:${CLASS_COLORS[classIdx(r.p[0])]}"><span class="gia-dot" aria-hidden="true"></span><span class="grow">Giá đất ở VT1: <b>${v}</b> tr/m²<small class="meta"> · ${esc(name)}</small></span>${icon('chevron-right', 'sm')}</button>`;
}

/** Panel actions (data-act="gia-…"); returns true when handled. */
export function giaClick(t, s, { rerender, padding }) {
  switch (t.dataset.act) {
    case 'gia-land': land = +t.dataset.k || 0; act('gia', 'land' + land); rerender(); return true;
    case 'gia-retry': rerender(true); return true;
    case 'gia-row': showRow(+t.dataset.a, +t.dataset.i, padding); return true;
    case 'gia-table': openGiaTable({ a: +t.dataset.a }); return true;
    case 'gia-layer': setGiaLayer(!giaLayerOn()); rerender(); return true;
    default: return false;
  }
}

/** Highlight a row's estimated segment on the map and fit it. */
export async function showRow(a, i, padding) {
  const A = await loadArea(a);
  const g = rowGeometry(A, i);
  const r = rowOf(A, i);
  if (!g) { toast(`${rowTitle(r)}: chưa định vị được đoạn này trên bản đồ`, { type: 'info', ms: 2600 }); return; }
  act('gia', 'row');
  pm.highlight(g, null);
  pm.fitBBox(geomBBox(g), { padding: padding?.(), maxZoom: 17 });
}

/* ---------------- map layer ---------------- */

export const giaLayerOn = () => prefs.gia === true;
const feats = new Map(); // a -> Feature[]
let layerTimer = 0;

export function setGiaLayer(on) {
  prefs.gia = !!on;
  savePrefs();
  act('gia', on ? 'layer-on' : 'layer-off');
  pm?.setGiaVisible(prefs.gia);
  bus.emit('gia-layer', prefs.gia);
  refreshLayer();
}

function areaFeatures(A) {
  if (feats.has(A.a)) return feats.get(A.a);
  const out = A.lines.map(([ids, c]) => {
    const rs = ids.map((i) => rowOf(A, i));
    const ps = rs.map((r) => r.p[0]).filter((v) => v != null);
    const lo = Math.min(...ps), hi = Math.max(...ps);
    const r = rs[0];
    const name = r.rural ? (r.f || r.s).replace(/\s*[:(].*$/, '').slice(0, 40) : r.s.replace(/\s*\(.*$/, '');
    const coords = [];
    for (let k = 0; k < c.length; k += 2) coords.push([c[k], c[k + 1]]);
    return {
      type: 'Feature',
      properties: { p: hi, amb: ids.length > 1, n: `${name} · ${lo === hi ? tr(hi) : tr(lo) + '–' + tr(hi)} tr/m²` },
      geometry: { type: 'LineString', coordinates: coords },
    };
  });
  feats.set(A.a, out);
  return out;
}

function areaBBoxes() {
  const bb = new Map();
  for (const w of index.wards) {
    const b = bb.get(w.a) || [180, 90, -180, -90];
    bb.set(w.a, [Math.min(b[0], w.b[0]), Math.min(b[1], w.b[1]), Math.max(b[2], w.b[2]), Math.max(b[3], w.b[3])]);
  }
  return bb;
}
let abb = null;

async function refreshLayer() {
  console.log('RL start', !!pm?.map, giaLayerOn(), pm?.map?.getZoom());
  if (!pm?.map) return;
  if (!giaLayerOn()) { pm.setData('gia', null); return; }
  try { await loadGiaIndex(); } catch { return; }
  abb ||= areaBBoxes();
  const m = pm.map;
  if (m.getZoom() < 10.5) { pm.setData('gia', null); return; }
  const b = m.getBounds();
  const [w, s, e, n] = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];
  const want = [...abb].filter(([, x]) => x[0] <= e && x[2] >= w && x[1] <= n && x[3] >= s).map(([a]) => a);
  console.log('RL want', JSON.stringify(want), JSON.stringify([w,s,e,n]), abb.size);
  const list = await Promise.all(want.map((a) => loadArea(a).catch(() => null)));
  if (!giaLayerOn()) return;
  pm.setData('gia', { type: 'FeatureCollection', features: list.filter(Boolean).flatMap(areaFeatures) });
}

/** Legend chips for the layer drawer. */
export function giaLegend() {
  const lab = ['< 10', '10–20', '20–40', '40–70', '70–120', '120–200', '200–300', '≥ 300'];
  return `<div class="gia-legend" aria-label="Chú giải giá đất ở VT1 (triệu đồng/m²)">${CLASS_COLORS.map((c, i) => `<span><i style="background:${c}"></i>${lab[i]}</span>`).join('')}<small>triệu đồng/m², đất ở VT1 · nét mờ: đường nhiều đoạn chưa tách</small></div>`;
}

/* ---------------- table dialog ---------------- */

let dlg = null;
const tb = { a: 1, q: '', land: 0 };
let shown = [];

function dialogShell() {
  dlg.innerHTML = `
    <header class="cat-head">
      <div class="cat-head-text">
        <span class="eyebrow">${icon('banknote')}Bảng giá đất 2026</span>
        <h2 id="gia-title">Bảng giá đất TP Hà Nội</h2>
        <p class="cat-count" id="gia-count">Nghị quyết 52/2025/NQ-HĐND · áp dụng từ 01/01/2026</p>
      </div>
      <button type="button" class="icon-btn ghost" data-close aria-label="Đóng">${icon('x')}</button>
    </header>
    <div class="cat-tools gia-tools">
      <label class="gia-area"><span class="sr-only">Khu vực</span><select id="gia-area"></select></label>
      <div class="cat-search">
        ${icon('search')}
        <input id="gia-q" type="text" inputmode="search" enterkeyhint="search" placeholder="Tên đường, phố, đoạn…" autocomplete="off" autocapitalize="off" spellcheck="false" aria-label="Tìm trong bảng giá">
      </div>
      <div class="seg gia-dland" role="tablist" aria-label="Loại đất" id="gia-dland"></div>
    </div>
    <p class="gia-wards" id="gia-wards"></p>
    <div class="gia-dlg-body" id="gia-body"></div>
    <p class="gia-foot">Đơn vị: nghìn đồng/m² (như trong bảng giá). VT1–VT4: vị trí 1–4. ${icon('map-pinned', 'sm')} = có vị trí ước tính trên bản đồ, bấm để xem.</p>`;
  dlg.addEventListener('click', onDlgClick);
  dlg.addEventListener('input', (e) => { if (e.target.id === 'gia-q') { tb.q = e.target.value; renderTable(); } });
  dlg.addEventListener('change', (e) => { if (e.target.id === 'gia-area') { tb.a = +e.target.value; renderTable(); } });
}

export async function openGiaTable({ a, q } = {}) {
  dlg ||= $('#gia-modal');
  if (!dlg) return;
  if (!dlg.firstChild) dialogShell();
  if (a) tb.a = a;
  if (q != null) tb.q = q;
  tb.land = land;
  act('gia', 'table');
  if (!dlg.open) dlg.showModal();
  $('#gia-q', dlg).value = tb.q;
  $('#gia-body', dlg).innerHTML = `<div class="cat-empty">${icon('loader-circle', 'spin')}Đang tải bảng giá…</div>`;
  try { await loadGiaIndex(); } catch (e) { $('#gia-body', dlg).innerHTML = `<div class="cat-empty">${icon('circle-alert')}Không tải được bảng giá đất.</div>`; return; }
  $('#gia-area', dlg).innerHTML = index.areas.map((x) => `<option value="${x.a}"${x.a === tb.a ? ' selected' : ''}>Khu vực ${x.a}</option>`).join('');
  renderTable();
  if (!isMobile()) $('#gia-q', dlg).focus();
}

async function renderTable() {
  const a = tb.a;
  const ai = areaInfo(a);
  $('#gia-wards', dlg).textContent = ai ? `Khu vực ${a}: ${ai.w}` : '';
  $('#gia-dland', dlg).innerHTML = LAND.map(([, l], k) => `<button type="button" role="tab" data-act="t-land" data-k="${k}" aria-selected="${tb.land === k}">${esc(l.replace('Đất sản xuất, kinh doanh phi nông nghiệp', 'Đất SXKD phi nông nghiệp'))}</button>`).join('');
  let A;
  try { A = await loadArea(a); } catch { $('#gia-body', dlg).innerHTML = `<div class="cat-empty">${icon('circle-alert')}Không tải được bảng giá khu vực ${a}.</div>`; return; }
  if (a !== tb.a) return;
  shown = searchRows(A, tb.q);
  const o = tb.land * 4;
  const MAX = 600;
  let sec = null;
  const body = shown.slice(0, MAX).map((r) => {
    let head = '';
    const grp = A.rural ? (r.sec || '') : '';
    if (grp && grp !== sec) { sec = grp; head = `<tr class="sec"><td colspan="6">${esc(grp)}</td></tr>`; }
    const geo = r.g === 1 || r.g === 2;
    const title = rowTitle(r), seg = rowSegment(r);
    return `${head}<tr${geo ? ` class="geo" data-act="t-row" data-i="${r.i}" tabindex="0"` : ''}>
      <td class="tt">${esc(r.tt)}</td>
      <td class="nm"><b>${esc(title)}</b>${geo ? icon('map-pinned', 'sm') : ''}${seg ? `<small>${esc(seg)}</small>` : ''}</td>
      ${[0, 1, 2, 3].map((k) => `<td class="num${k === 0 ? ' vt1' : ''}">${ngh(r.p[o + k])}</td>`).join('')}
    </tr>`;
  }).join('');
  $('#gia-count', dlg).textContent = `Khu vực ${a} · ${fmt.num(shown.length)}${tb.q ? ' kết quả' : ' dòng'}${shown.length > MAX ? ` (hiện ${MAX})` : ''} · NQ 52/2025/NQ-HĐND`;
  $('#gia-body', dlg).innerHTML = shown.length
    ? `<table class="gia-t gia-big"><thead><tr><th>TT</th><th>Tên đường, phố · đoạn</th><th>VT1</th><th>VT2</th><th>VT3</th><th>VT4</th></tr></thead><tbody>${body}</tbody></table>`
    : `<div class="cat-empty">${icon('search')}Không có dòng nào khớp “${esc(tb.q)}” trong khu vực ${a}.</div>`;
}

function onDlgClick(e) {
  if (e.target === dlg || e.target.closest('[data-close]')) { dlg.close(); return; }
  const t = e.target.closest('[data-act]');
  if (!t) return;
  if (t.dataset.act === 't-land') { tb.land = +t.dataset.k; renderTable(); }
  if (t.dataset.act === 't-row') { dlg.close(); showRow(tb.a, +t.dataset.i, padFn); }
}

/* ---------------- init ---------------- */

let padFn = null;
export function initGia(planMap, { padding } = {}) {
  pm = planMap;
  padFn = padding || null;
  pm.setGiaVisible(giaLayerOn());
  pm.map.on('moveend', () => { if (!giaLayerOn()) return; clearTimeout(layerTimer); layerTimer = setTimeout(refreshLayer, 120); });
  bus.on('gia-table', (o) => openGiaTable(o || {}));
  if (giaLayerOn()) refreshLayer();
  document.addEventListener('keydown', (e) => { if (e.key === 'Enter' && dlg?.open && e.target.matches?.('tr[data-act="t-row"]')) e.target.click(); });
}

